// =============================================================================
// SIREPPAR - Página de Gestión de Usuarios
// =============================================================================

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import DataTable, { Column } from '../../components/common/DataTable';
import Modal from '../../components/common/Modal';
import { usuarioService, partidoService, padronService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

// =============================================================================
// Tipos
// =============================================================================

interface Usuario {
  id: string;
  email: string;
  nombre: string;
  apellido: string;
  dni: string;
  telefono?: string;
  rol: string;
  estado: string;
  mfaHabilitado: boolean;
  ultimoAcceso?: string;
  creadoEn: string;
  partido?: { id: string; denominacion: string; sigla: string };
}

// =============================================================================
// Constantes
// =============================================================================

const roles = [
  { value: 'ADMINISTRADOR', label: 'Administrador', tipo: 'interno' },
  { value: 'SUPERVISOR', label: 'Supervisor', tipo: 'interno' },
  { value: 'ANALISTA', label: 'Analista', tipo: 'interno' },
  { value: 'MESA_ENTRADAS', label: 'Mesa de Entradas', tipo: 'interno' },
  { value: 'AUTORIDAD_FIRMANTE', label: 'Autoridad Firmante', tipo: 'interno' },
  { value: 'AUDITOR', label: 'Auditor', tipo: 'interno' },
  { value: 'CARGADOR', label: 'Cargador', tipo: 'externo' },
  { value: 'APODERADO_FIRMANTE', label: 'Apoderado Firmante', tipo: 'externo' },
  { value: 'PRESIDENTE_FIRMANTE', label: 'Presidente Firmante', tipo: 'externo' },
  { value: 'VICEPRESIDENTE_FIRMANTE', label: 'Vicepresidente Firmante', tipo: 'externo' },
  { value: 'CONSULTA_EXTERNA', label: 'Consulta Externa', tipo: 'externo' },
];

const rolesPermitidosApoderado = [
  'APODERADO_FIRMANTE',
  'PRESIDENTE_FIRMANTE',
  'VICEPRESIDENTE_FIRMANTE',
];

const rolConfig: Record<string, { color: string; label: string }> = {
  ADMINISTRADOR: { color: 'bg-red-100 text-red-800', label: 'Administrador' },
  SUPERVISOR: { color: 'bg-purple-100 text-purple-800', label: 'Supervisor' },
  ANALISTA: { color: 'bg-blue-100 text-blue-800', label: 'Analista' },
  MESA_ENTRADAS: { color: 'bg-cyan-100 text-cyan-800', label: 'Mesa Entradas' },
  AUTORIDAD_FIRMANTE: { color: 'bg-yellow-100 text-yellow-800', label: 'Aut. Firmante' },
  AUDITOR: { color: 'bg-gray-100 text-gray-800', label: 'Auditor' },
  CARGADOR: { color: 'bg-teal-100 text-teal-800', label: 'Cargador' },
  APODERADO_FIRMANTE: { color: 'bg-green-100 text-green-800', label: 'Apod. Firmante' },
  PRESIDENTE_FIRMANTE: { color: 'bg-emerald-100 text-emerald-800', label: 'Pres. Firmante' },
  VICEPRESIDENTE_FIRMANTE: { color: 'bg-teal-100 text-teal-800', label: 'Vicepres. Firmante' },
  CONSULTA_EXTERNA: { color: 'bg-gray-100 text-gray-600', label: 'Consulta' },
};

const estadoConfig: Record<string, { color: string; label: string }> = {
  ACTIVO: { color: 'bg-green-100 text-green-800', label: 'Activo' },
  INACTIVO: { color: 'bg-gray-100 text-gray-800', label: 'Inactivo' },
  BLOQUEADO: { color: 'bg-red-100 text-red-800', label: 'Bloqueado' },
  PENDIENTE_VERIFICACION: { color: 'bg-yellow-100 text-yellow-800', label: 'Pendiente' },
};

// =============================================================================
// Componente
// =============================================================================

export default function UsuariosPage() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const esRolExterno = ['APODERADO_FIRMANTE', 'PRESIDENTE_FIRMANTE', 'VICEPRESIDENTE_FIRMANTE'].includes(user?.rol || '');

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [rolFilter, setRolFilter] = useState('');
  const [estadoFilter, setEstadoFilter] = useState('');
  const [tipoFilter, setTipoFilter] = useState<'todos' | 'internos' | 'externos'>('todos');
  const [partidoFilter, setPartidoFilter] = useState('');
  
  const [showModal, setShowModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [showSessionsModal, setShowSessionsModal] = useState(false);
  const [editingUser, setEditingUser] = useState<Usuario | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [buscandoDni, setBuscandoDni] = useState(false);
  const [dniLookupError, setDniLookupError] = useState('');
  /**
   * Qué devolvió la búsqueda del DNI en la nómina de la agrupación.
   *
   * `usuario` presente = esa persona YA tiene cuenta en el sistema, y entonces la
   * pantalla no crea otra (`usuarios.dni` es único: una persona, un usuario). Le da
   * ACCESO a esta agrupación con la cuenta que ya tiene. Ver `vincularAgrupacion`
   * en el backend.
   */
  const [miembro, setMiembro] = useState<{
    usuario: { email: string; enmascarado: boolean; telefono?: string; rol: string; estado: string; esDeEstaAgrupacion: boolean } | null;
    accesoAEstaAgrupacion: string | null;
  } | null>(null);
  /** Modo vinculación: la persona ya tiene usuario y sólo hay que darle acceso. */
  const modoVincular = !editingUser && !!miembro?.usuario;
  const yaTieneAcceso = miembro?.accesoAEstaAgrupacion === 'ACTIVA';
  /**
   * El DNI ya identificó a la persona: sus datos filiatorios NO se tipean.
   *
   * Salen de la nómina de la agrupación o del padrón, que es de donde el backend los
   * vuelve a tomar al guardar (ver `conciliarPersonaPorDni`). Editables sólo
   * invitaban a escribir algo que después se descartaba en silencio.
   */
  const personaIdentificada = !editingUser && !!miembro;

  // Form state
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    nombre: '',
    apellido: '',
    dni: '',
    telefono: '',
    rol: 'ANALISTA',
    estado: 'ACTIVO',
    partidoId: '',
  });

  // Query usuarios
  const { data, isLoading } = useQuery({
    queryKey: ['usuarios', page, pageSize, search, rolFilter, estadoFilter, partidoFilter, tipoFilter],
    queryFn: async () => {
      const params: Record<string, any> = { page, pageSize };
      if (search) params.search = search;
      if (rolFilter) params.rol = rolFilter;
      if (estadoFilter) params.estado = estadoFilter;
      if (partidoFilter) params.partidoId = partidoFilter;
      const response = await usuarioService.list(params);
        return response.data || { data: [], meta: { totalItems: 0 } };
    },
  });

  // Filtrar por tipo (interno/externo) en frontend
  const usuarios = Array.isArray(data?.data) ? data.data : [];
  const filteredData = usuarios.filter((u: Usuario) => {
    if (tipoFilter === 'todos') return true;
    const rolInfo = roles.find(r => r.value === u.rol);
    if (tipoFilter === 'internos') return rolInfo?.tipo === 'interno';
    if (tipoFilter === 'externos') return rolInfo?.tipo === 'externo';
    return true;
  }) || [];

  // Query partidos para filtros
  const { data: partidos } = useQuery({
    queryKey: ['partidos-select'],
    queryFn: async () => {
      const response = await partidoService.list({ pageSize: 500 });
      return response.data.data;
    },
  });

  // Query sesiones
  const { data: sessions, isLoading: loadingSessions } = useQuery({
    queryKey: ['user-sessions', selectedUserId],
    queryFn: async () => {
      if (!selectedUserId) return [];
      const response = await usuarioService.getSessions(selectedUserId);
      return response.data.data || [];
    },
    enabled: !!selectedUserId && showSessionsModal,
  });

  // Mutations
  const saveMutation = useMutation({
    mutationFn: async (data: any) => {
      if (editingUser) return usuarioService.update(editingUser.id, data);
      // La persona ya tiene cuenta: se le otorga acceso a esta agrupación, no se
      // crea un usuario nuevo.
      if (data.__vincular) {
        return usuarioService.vincularAgrupacion({ dni: data.dni, rol: data.rol });
      }
      return usuarioService.create(data);
    },
    onSuccess: (_r, vars: any) => {
      toast.success(
        editingUser ? 'Usuario actualizado'
          : vars?.__vincular ? 'Ya puede trabajar con esta agrupación desde su usuario'
          : 'Usuario creado',
      );
      queryClient.invalidateQueries({ queryKey: ['usuarios'] });
      closeModal();
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error?.message || 'Error al guardar');
    },
  });

  const resetPasswordMutation = useMutation({
    mutationFn: ({ id, password }: { id: string; password: string }) => 
      usuarioService.resetPassword(id, password),
    onSuccess: () => {
      toast.success('Contraseña reseteada exitosamente');
      setShowResetModal(false);
      setNewPassword('');
      setSelectedUserId(null);
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error?.message || 'Error al resetear contraseña');
    },
  });

  const unlockMutation = useMutation({
    mutationFn: (id: string) => usuarioService.unlock(id),
    onSuccess: () => {
      toast.success('Usuario desbloqueado');
      queryClient.invalidateQueries({ queryKey: ['usuarios'] });
    },
    onError: () => {
      toast.error('Error al desbloquear usuario');
    },
  });

  const revokeSessionsMutation = useMutation({
    mutationFn: (id: string) => usuarioService.revokeSessions(id),
    onSuccess: () => {
      toast.success('Sesiones revocadas');
      queryClient.invalidateQueries({ queryKey: ['user-sessions'] });
      setShowSessionsModal(false);
    },
    onError: () => {
      toast.error('Error al revocar sesiones');
    },
  });

  const disableMFAMutation = useMutation({
    mutationFn: (id: string) => usuarioService.disableMFA(id),
    onSuccess: () => {
      toast.success('MFA deshabilitado exitosamente');
      queryClient.invalidateQueries({ queryKey: ['usuarios'] });
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error?.message || 'Error al deshabilitar MFA');
    },
  });

  /**
   * Agrupaciones del usuario que se está editando (dato informativo del modal).
   * Viene de `GET /usuarios/:id`, que las trae junto con el resto.
   */
  const { data: agrupacionesDelUsuario } = useQuery({
    queryKey: ['usuario-agrupaciones', editingUser?.id],
    queryFn: async () => {
      const r = await usuarioService.getById(editingUser!.id);
      return (r.data.data?.agrupaciones ?? []) as Array<{
        estado: string;
        esPrincipal: boolean;
        rol: string | null;
        partido: { id: string; denominacion: string; sigla?: string; clase: string };
      }>;
    },
    enabled: !!editingUser?.id && showModal,
  });

  const openModal = (usuario?: Usuario) => {
    setDniLookupError('');
    if (usuario) {
      setEditingUser(usuario);
      setFormData({
        email: usuario.email,
        password: '',
        nombre: usuario.nombre,
        apellido: usuario.apellido,
        dni: usuario.dni,
        telefono: usuario.telefono || '',
        rol: usuario.rol,
        estado: usuario.estado,
        partidoId: usuario.partido?.id || '',
      });
    } else {
      setEditingUser(null);
      setFormData({
        email: '',
        password: '',
        nombre: '',
        apellido: '',
        dni: '',
        telefono: '',
        rol: esRolExterno ? 'APODERADO_FIRMANTE' : 'ANALISTA',
        estado: 'ACTIVO',
        partidoId: esRolExterno ? ((user as any)?.partidoId || '') : '',
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setDniLookupError('');
    setMiembro(null);
  };

  const handleDniBuscar = async () => {
    if (editingUser) return; // No buscar al editar
    const dni = formData.dni.trim();
    if (dni.length < 7) return;

    setDniLookupError('');
    setMiembro(null);
    setBuscandoDni(true);
    try {
      if (esRolExterno) {
        // Rol externo: buscar en autoridades/apoderados del partido
        const partidoIdBuscar = user?.partidoId;
        if (!partidoIdBuscar) return;
        const resp = await partidoService.buscarMiembro(partidoIdBuscar, dni);
        const datos = resp.data.data;
        setMiembro({ usuario: datos.usuario ?? null, accesoAEstaAgrupacion: datos.accesoAEstaAgrupacion ?? null });
        setFormData(f => ({
          ...f,
          nombre: datos.nombre,
          apellido: datos.apellido,
          // Si ya tiene usuario, se muestra el correo y el teléfono DE SU CUENTA, no
          // los de la nómina: son los que va a seguir usando para entrar. Y van de
          // sólo lectura, porque esta pantalla no le cambia el acceso a nadie.
          email: datos.usuario?.email || datos.email || f.email,
          telefono: datos.usuario?.telefono || datos.telefono || f.telefono,
        }));
      } else {
        // Rol interno: buscar en padrón electoral
        const resp = await padronService.validarDni(dni);
        const resultado = resp.data.data;
        if (resultado.valido && resultado.datos) {
          setFormData(f => ({
            ...f,
            nombre: resultado.datos.nombre,
            apellido: resultado.datos.apellido,
          }));
        }
      }
    } catch (err: any) {
      const msg = err.response?.data?.error?.message || 'DNI no encontrado';
      if (esRolExterno) {
        setDniLookupError(msg);
      }
    } finally {
      setBuscandoDni(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (esRolExterno && !editingUser && dniLookupError) {
      toast.error('El DNI no está registrado en las nóminas del partido');
      return;
    }
    // Ya tiene usuario: sólo hace falta el DNI y el rol.
    if (modoVincular) {
      if (yaTieneAcceso) {
        toast.error('Esa persona ya tiene acceso a esta agrupación');
        return;
      }
      saveMutation.mutate({ __vincular: true, dni: formData.dni, rol: formData.rol });
      return;
    }
    const partidoIdFinal = esRolExterno ? user?.partidoId : (formData.partidoId || undefined);
    if (!editingUser && isExternoRol && !partidoIdFinal) {
      toast.error('Debe seleccionar un partido para el rol externo');
      return;
    }
    const payload: any = {
      nombre: formData.nombre,
      apellido: formData.apellido,
      dni: formData.dni,
      telefono: formData.telefono || undefined,
      rol: formData.rol,
      estado: formData.estado,
      partidoId: partidoIdFinal,
    };
    if (!editingUser) {
      payload.email = formData.email;
      payload.password = formData.password;
    } else if (['ADMINISTRADOR', 'SUPERVISOR'].includes(user?.rol || '') && formData.email !== editingUser.email) {
      // Admin/Supervisor pueden cambiar el email
      payload.email = formData.email;
    }
    saveMutation.mutate(payload);
  };

  const openResetModal = (userId: string) => {
    setSelectedUserId(userId);
    setNewPassword('');
    setShowResetModal(true);
  };

  const openSessionsModal = (userId: string) => {
    setSelectedUserId(userId);
    setShowSessionsModal(true);
  };

  const handleResetPassword = () => {
    if (!selectedUserId || !newPassword) return;
    if (newPassword.length < 8) {
      toast.error('La contraseña debe tener al menos 8 caracteres');
      return;
    }
    resetPasswordMutation.mutate({ id: selectedUserId, password: newPassword });
  };

  const isExternoRol = roles.find(r => r.value === formData.rol)?.tipo === 'externo';

  // Columns
  /**
   * Las acciones de la fila (Editar, Reset Pass, Desbloquear, Sesiones) son TODAS
   * de rol interno: sus endpoints exigen ADMINISTRADOR/SUPERVISOR. Al apoderado se
   * le mostraban igual y cada botón le devolvía 403. Ver `columnasVisibles`.
   */
  const columns: Column<Usuario>[] = [
    {
      key: 'nombre',
      header: 'Usuario',
      render: (_, row) => (
        <div>
          <p className="font-medium text-gray-900">{row.nombre} {row.apellido}</p>
          <p className="text-sm text-gray-500">{row.email}</p>
        </div>
      ),
    },
    {
      key: 'dni',
      header: 'DNI',
      width: '100px',
    },
    {
      key: 'rol',
      header: 'Rol',
      width: '140px',
      render: (value) => {
        const config = rolConfig[value] || { color: 'bg-gray-100 text-gray-600', label: value };
        return (
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
            {config.label}
          </span>
        );
      },
    },
    {
      key: 'partido',
      header: 'Partido',
      render: (value) => value ? (
        <span className="text-sm" title={value.denominacion}>{value.sigla}</span>
      ) : (
        <span className="text-gray-400">-</span>
      ),
    },
    {
      key: 'estado',
      header: 'Estado',
      width: '110px',
      render: (value) => {
        const config = estadoConfig[value] || { color: 'bg-gray-100 text-gray-600', label: value };
        return (
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
            {config.label}
          </span>
        );
      },
    },
    {
      key: 'mfaHabilitado',
      header: 'MFA',
      width: '60px',
      render: (value) => value ? '🔒' : '—',
    },
    {
      key: 'ultimoAcceso',
      header: 'Último Acceso',
      width: '120px',
      render: (value) => value ? new Date(value).toLocaleDateString('es-AR') : 'Nunca',
    },
    {
      key: 'actions',
      header: 'Acciones',
      width: '200px',
      render: (_, row) => (
        <div className="flex gap-1 flex-wrap">
          <button
            onClick={(e) => { e.stopPropagation(); openModal(row); }}
            className="px-2 py-1 text-xs bg-blue-50 text-blue-700 rounded hover:bg-blue-100"
          >
            Editar
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); openResetModal(row.id); }}
            className="px-2 py-1 text-xs bg-orange-50 text-orange-700 rounded hover:bg-orange-100"
          >
            Reset Pass
          </button>
          {row.estado === 'BLOQUEADO' && (
            <button
              onClick={(e) => { e.stopPropagation(); unlockMutation.mutate(row.id); }}
              className="px-2 py-1 text-xs bg-green-50 text-green-700 rounded hover:bg-green-100"
            >
              Desbloquear
            </button>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); openSessionsModal(row.id); }}
            className="px-2 py-1 text-xs bg-purple-50 text-purple-700 rounded hover:bg-purple-100"
          >
            Sesiones
          </button>
          {row.mfaHabilitado && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (confirm(`¿Está seguro de deshabilitar MFA para ${row.nombre} ${row.apellido}?`)) {
                  disableMFAMutation.mutate(row.id);
                }
              }}
              className="px-2 py-1 text-xs bg-red-50 text-red-700 rounded hover:bg-red-100"
            >
              Deshabilitar MFA
            </button>
          )}
        </div>
      ),
    },
  ];

  // Al externo se le saca la columna de acciones completa (ver la nota de arriba).
  const columnasVisibles = esRolExterno ? columns.filter((c) => c.key !== 'actions') : columns;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Usuarios</h1>
          <p className="mt-1 text-sm text-gray-500">Gestión de usuarios del sistema</p>
        </div>
        <button
          onClick={() => openModal()}
          className="mt-4 sm:mt-0 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          + Nuevo Usuario
        </button>
      </div>

      {/* Filters */}
      {!esRolExterno && (
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <input
              type="text"
              placeholder="Buscar nombre, email, DNI..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <select
              value={tipoFilter}
              onChange={(e) => { setTipoFilter(e.target.value as any); setPage(1); }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value="todos">Todos los tipos</option>
              <option value="internos">Usuarios Internos</option>
              <option value="externos">Usuarios Externos</option>
            </select>
          </div>
          <div>
            <select
              value={rolFilter}
              onChange={(e) => { setRolFilter(e.target.value); setPage(1); }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value="">Todos los roles</option>
              <optgroup label="Internos">
                {roles.filter(r => r.tipo === 'interno').map(r => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </optgroup>
              <optgroup label="Externos">
                {roles.filter(r => r.tipo === 'externo').map(r => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </optgroup>
            </select>
          </div>
          <div>
            <select
              value={estadoFilter}
              onChange={(e) => { setEstadoFilter(e.target.value); setPage(1); }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value="">Todos los estados</option>
              <option value="ACTIVO">Activo</option>
              <option value="INACTIVO">Inactivo</option>
              <option value="BLOQUEADO">Bloqueado</option>
              <option value="PENDIENTE_VERIFICACION">Pendiente</option>
            </select>
          </div>
          <div>
            <select
              value={partidoFilter}
              onChange={(e) => { setPartidoFilter(e.target.value); setPage(1); }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value="">Todos los partidos</option>
              {partidos?.map((p: any) => (
                <option key={p.id} value={p.id}>{p.numero} - {p.sigla}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
      )}

      {/* Stats */}
      {!esRolExterno && (
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Total</p>
          <p className="text-2xl font-bold">{data?.meta?.totalItems || 0}</p>
        </div>
        <div className="bg-green-50 rounded-lg shadow p-4">
          <p className="text-sm text-green-600">Activos</p>
          <p className="text-2xl font-bold text-green-700">{filteredData.filter((u: Usuario) => u.estado === 'ACTIVO').length}</p>
        </div>
        <div className="bg-red-50 rounded-lg shadow p-4">
          <p className="text-sm text-red-600">Bloqueados</p>
          <p className="text-2xl font-bold text-red-700">{filteredData.filter((u: Usuario) => u.estado === 'BLOQUEADO').length}</p>
        </div>
        <div className="bg-yellow-50 rounded-lg shadow p-4">
          <p className="text-sm text-yellow-600">Pendientes</p>
          <p className="text-2xl font-bold text-yellow-700">{filteredData.filter((u: Usuario) => u.estado === 'PENDIENTE_VERIFICACION').length}</p>
        </div>
      </div>
      )}

      {/* Table */}
      <DataTable
        data={filteredData}
        columns={columnasVisibles}
        loading={isLoading}
        emptyMessage="No se encontraron usuarios"
        pagination={{
          page,
          pageSize,
          total: data?.meta?.totalItems || 0,
          onPageChange: setPage,
          onPageSizeChange: setPageSize,
        }}
      />

      {/* Modal Crear/Editar */}
      <Modal
        isOpen={showModal}
        onClose={closeModal}
        title={editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}
        size="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {/* DNI — siempre primero */}
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">DNI *</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={formData.dni}
                  onChange={(e) => {
                    setFormData({ ...formData, dni: e.target.value });
                    setDniLookupError('');
                  }}
                  onBlur={handleDniBuscar}
                  required
                  disabled={!!editingUser}
                  className="w-full border rounded-lg px-3 py-2 disabled:bg-gray-100"
                  placeholder="Ingrese DNI para autocompletar"
                />
                {buscandoDni && (
                  <div className="flex items-center px-3">
                    <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full" />
                  </div>
                )}
              </div>
              {dniLookupError && (
                <p className="text-red-600 text-xs mt-1">{dniLookupError}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Nombre *</label>
              <input
                type="text"
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                required={!modoVincular}
                disabled={(esRolExterno && !editingUser) || personaIdentificada || !!editingUser}
                className="w-full border rounded-lg px-3 py-2 disabled:bg-gray-100 disabled:text-gray-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Apellido *</label>
              <input
                type="text"
                value={formData.apellido}
                onChange={(e) => setFormData({ ...formData, apellido: e.target.value })}
                required={!modoVincular}
                disabled={(esRolExterno && !editingUser) || personaIdentificada || !!editingUser}
                className="w-full border rounded-lg px-3 py-2 disabled:bg-gray-100 disabled:text-gray-500"
              />
            </div>

            {/* Ya tiene cuenta: su correo, contraseña y teléfono se muestran de SÓLO
                LECTURA. Editables, esta pantalla le estaría cambiando el acceso que
                ya usa — y para el que crea no hay nada que decidir ahí. */}
            {modoVincular && (
              <div className="col-span-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
                <p>
                  Esta persona ya tiene usuario en el sistema
                  {miembro?.usuario?.esDeEstaAgrupacion ? '' : ' (de otra agrupación)'}. Entra con esa
                  misma cuenta: no se le crea otra ni se le cambia nada.
                </p>
                <p className="mt-1">
                  {yaTieneAcceso
                    ? 'Ya tiene acceso a esta agrupación.'
                    : 'Al darle acceso podrá elegir esta agrupación desde el selector.'}
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-1">
                Email *
                {editingUser && ['ADMINISTRADOR', 'SUPERVISOR'].includes(user?.rol || '') && (
                  <span className="ml-1 text-xs text-blue-600">(editable)</span>
                )}
                {modoVincular && <span className="ml-1 text-xs text-gray-500">(su cuenta)</span>}
              </label>
              <input
                type={modoVincular ? 'text' : 'email'}
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required={!editingUser && !modoVincular}
                disabled={modoVincular || (!!editingUser && !['ADMINISTRADOR', 'SUPERVISOR'].includes(user?.rol || ''))}
                className="w-full border rounded-lg px-3 py-2 disabled:bg-gray-100 disabled:text-gray-500 disabled:font-mono"
              />
            </div>

            {modoVincular && (
              <div>
                <label className="block text-sm font-medium mb-1">
                  Contraseña <span className="text-xs text-gray-500">(la de su cuenta)</span>
                </label>
                {/* Texto, nunca un input de contraseña: no hay contraseña que mostrar
                    —el backend no la manda ni podría— y un campo de ese tipo lo
                    rellenan y lo leen los gestores de contraseñas. */}
                <input
                  type="text"
                  value="••••••••••"
                  disabled
                  className="w-full border rounded-lg px-3 py-2 bg-gray-100 text-gray-500"
                />
                <p className="text-xs text-gray-500 mt-1">No se muestra ni se cambia: sigue usando la suya.</p>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium mb-1">Teléfono</label>
              <input
                type="text"
                value={formData.telefono}
                onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                disabled={modoVincular}
                className="w-full border rounded-lg px-3 py-2 disabled:bg-gray-100 disabled:text-gray-500"
              />
            </div>

            {!editingUser && !modoVincular && (
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Contraseña *</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  minLength={8}
                  className="w-full border rounded-lg px-3 py-2"
                  placeholder="Mínimo 8 caracteres"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-1">Rol *</label>
              <select
                value={formData.rol}
                onChange={(e) => setFormData({ ...formData, rol: e.target.value })}
                required
                className="w-full border rounded-lg px-3 py-2"
              >
                {esRolExterno || modoVincular ? (
                  roles.filter(r => rolesPermitidosApoderado.includes(r.value)).map(r => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))
                ) : (
                  <>
                    <optgroup label="Roles Internos">
                      {roles.filter(r => r.tipo === 'interno').map(r => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Roles Externos">
                      {roles.filter(r => r.tipo === 'externo').map(r => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </optgroup>
                  </>
                )}
              </select>
            </div>
            {!esRolExterno && (
              <div>
                <label className="block text-sm font-medium mb-1">Estado *</label>
                <select
                  value={formData.estado}
                  onChange={(e) => setFormData({ ...formData, estado: e.target.value })}
                  required
                  className="w-full border rounded-lg px-3 py-2"
                >
                  <option value="ACTIVO">Activo</option>
                  <option value="INACTIVO">Inactivo</option>
                  <option value="BLOQUEADO">Bloqueado</option>
                  <option value="PENDIENTE_VERIFICACION">Pendiente Verificación</option>
                </select>
              </div>
            )}

            {/* EDICIÓN: las agrupaciones son un DATO, no un campo.
                El desplegable de partido sugería que la pertenencia se cambia acá, y
                no es así: se otorga o se revoca por membresía
                (`usuarios_agrupaciones`), que es lo que decide qué puede administrar
                la persona y con qué rol en cada una. Un `select` de un solo partido
                además no podía representar a alguien que administra dos. */}
            {editingUser && (
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Agrupaciones</label>
                {agrupacionesDelUsuario === undefined ? (
                  <p className="text-sm text-gray-500">Cargando…</p>
                ) : agrupacionesDelUsuario.length === 0 ? (
                  <p className="text-sm text-gray-500">No administra ninguna agrupación.</p>
                ) : (
                  <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
                    {agrupacionesDelUsuario.map((a) => (
                      <li key={a.partido.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
                        <span>
                          <span className="font-medium text-gray-900">{a.partido.denominacion}</span>
                          {a.partido.sigla && <span className="text-gray-500"> ({a.partido.sigla})</span>}
                          {a.partido.clase !== 'ESTANDAR' && (
                            <span className="ml-2 text-xs text-gray-500 capitalize">{a.partido.clase.toLowerCase()}</span>
                          )}
                        </span>
                        <span className="flex items-center gap-2 text-xs">
                          {a.esPrincipal && (
                            <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-medium">Principal</span>
                          )}
                          <span className="text-gray-600">{rolConfig[a.rol || '']?.label || rolConfig[editingUser.rol]?.label}</span>
                          {a.estado !== 'ACTIVA' && (
                            <span className="px-2 py-0.5 rounded-full bg-gray-200 text-gray-700 capitalize">{a.estado.toLowerCase()}</span>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {/* ALTA de un rol externo: sigue haciendo falta elegir el partido. */}
            {!esRolExterno && !editingUser && isExternoRol && (
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Partido *</label>
                <select
                  value={formData.partidoId}
                  onChange={(e) => setFormData({ ...formData, partidoId: e.target.value })}
                  required
                  className="w-full border rounded-lg px-3 py-2"
                >
                  <option value="">Sin partido</option>
                  {partidos?.map((p: any) => (
                    <option key={p.id} value={p.id}>{p.numero} - {p.denominacion} ({p.sigla})</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button type="button" onClick={closeModal} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saveMutation.isPending || yaTieneAcceso}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg disabled:opacity-50 hover:bg-primary-700"
            >
              {saveMutation.isPending
                ? 'Guardando...'
                : editingUser ? 'Actualizar'
                : modoVincular ? 'Dar acceso'
                : 'Crear Usuario'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Modal Reset Password */}
      <Modal
        isOpen={showResetModal}
        onClose={() => { setShowResetModal(false); setNewPassword(''); }}
        title="Resetear Contraseña"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">Ingrese la nueva contraseña para el usuario:</p>
          <div>
            <label className="block text-sm font-medium mb-1">Nueva Contraseña *</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full border rounded-lg px-3 py-2"
              placeholder="Mínimo 8 caracteres"
              minLength={8}
            />
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={() => { setShowResetModal(false); setNewPassword(''); }}
              className="px-4 py-2 border rounded-lg hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              onClick={handleResetPassword}
              disabled={resetPasswordMutation.isPending || newPassword.length < 8}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg disabled:opacity-50 hover:bg-orange-700"
            >
              {resetPasswordMutation.isPending ? 'Reseteando...' : 'Resetear'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal Sesiones */}
      <Modal
        isOpen={showSessionsModal}
        onClose={() => setShowSessionsModal(false)}
        title="Sesiones Activas"
        size="lg"
      >
        <div className="space-y-4">
          {loadingSessions ? (
            <p className="text-center py-4">Cargando sesiones...</p>
          ) : sessions?.length === 0 ? (
            <p className="text-center py-4 text-gray-500">No hay sesiones activas</p>
          ) : (
            <div className="space-y-2">
              {sessions?.map((session: any) => (
                <div key={session.id} className="border rounded-lg p-3 bg-gray-50">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm font-medium">IP: {session.ip}</p>
                      <p className="text-xs text-gray-500 truncate max-w-md" title={session.userAgent}>
                        {session.userAgent}
                      </p>
                    </div>
                    <div className="text-right text-xs text-gray-500">
                      <p>Creada: {new Date(session.creadoEn).toLocaleString('es-AR')}</p>
                      <p>Expira: {new Date(session.expiraEn).toLocaleString('es-AR')}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="flex justify-between pt-4 border-t">
            <button
              onClick={() => selectedUserId && revokeSessionsMutation.mutate(selectedUserId)}
              disabled={revokeSessionsMutation.isPending || !sessions?.length}
              className="px-4 py-2 bg-red-600 text-white rounded-lg disabled:opacity-50 hover:bg-red-700"
            >
              {revokeSessionsMutation.isPending ? 'Revocando...' : 'Revocar Todas'}
            </button>
            <button
              onClick={() => setShowSessionsModal(false)}
              className="px-4 py-2 border rounded-lg hover:bg-gray-50"
            >
              Cerrar
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
